import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Trash2, TrendingUp } from 'lucide-react';
import financeService from '../services/financeService';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import toast from 'react-hot-toast';

const FinanceEntries = () => {
  const { user } = useAuth();
  const canManage = hasPermission(user?.role, 'financial:full');
  const canView = canManage || hasPermission(user?.role, 'financial:view');
  const queryClient = useQueryClient();

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    years.push(y);
  }

  const formatLocalDate = (dateStr) => {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string') {
      const pureDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const parts = pureDate.split('-');
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    const d = new Date(dateStr);
    return isNaN(d) ? '-' : d.toLocaleDateString('pt-BR');
  };

  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    payment_method: '',
    month: '',
    year: '',
    start_date: '',
    end_date: '',
    type: ''
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['finance-incomes', filters],
    queryFn: () => financeService.getIncomes(filters),
    keepPreviousData: true,
    staleTime: 0,
    refetchOnWindowFocus: true
  });
  const { data: methods } = useQuery({ queryKey: ['finance-methods'], queryFn: financeService.getPaymentMethods });

  const { data: totalsData } = useQuery({
    queryKey: ['finance-incomes-totals', filters],
    queryFn: () => financeService.getIncomesTotals(filters),
    staleTime: 0,
    refetchOnWindowFocus: true
  });

  const deleteMutation = useMutation({
    mutationFn: financeService.deleteIncome,
    onSuccess: () => { toast.success('Entrada removida'); queryClient.invalidateQueries(['finance-incomes']); queryClient.invalidateQueries(['finance-incomes-totals']); queryClient.invalidateQueries(['finance-stats']); },
    onError: (e) => toast.error(e?.response?.data?.message || 'Erro ao remover')
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ page: 1, limit: 20, search: '', payment_method: '', month: '', year: '', start_date: '', end_date: '', type: '' });
  };

  if (!canView) return <div className="p-6 text-red-600">Sem permissão para visualizar entradas.</div>;

  const items = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0, limit: 20 };

  return (
    <div className="p-6 space-y-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Entradas</h1>
          <p className="text-gray-500 mt-1">Visualize entradas de recebimentos</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Card Histórico */}
        <div className="group relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 border-slate-500 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-white/30 to-white/10" />
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-200 mb-1">Total Histórico</p>
              <p className="text-3xl font-bold text-white tracking-tight">
                R$ {parseFloat(totalsData?.data?.historical_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-300 mt-2">{totalsData?.data?.historical_count || 0} registros</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl group-hover:bg-white/30 transition-colors">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Card Filtrado */}
        <div className="group relative bg-gradient-to-br from-primary-600 to-primary-700 border-2 border-primary-500 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-white/30 to-white/10" />
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-primary-200 mb-1">Total Filtrado</p>
              <p className="text-3xl font-bold text-white tracking-tight">
                R$ {parseFloat(totalsData?.data?.filtered_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-primary-300 mt-2">{pagination.total} registros</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl group-hover:bg-white/30 transition-colors">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="bg-white border-2 border-gray-100 rounded-2xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Pesquisar..."
              className="input pl-9"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <select
            className="input"
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
          >
            <option value="">Todos os Tipos</option>
            <option value="REVENUE">Entradas (Recebimentos)</option>
            <option value="INVESTMENT">Investimentos/Patrimônios</option>
          </select>
          <select
            className="input"
            value={filters.payment_method}
            onChange={(e) => handleFilterChange('payment_method', e.target.value)}
          >
            <option value="">Todos os Métodos</option>
            {methods?.data?.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mt-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data Início</label>
            <input
              type="date"
              className="input"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data Fim</label>
            <input
              type="date"
              className="input"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Registros por página</label>
            <select
              className="input"
              value={filters.limit}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
          </div>
          <button
            onClick={handleClearFilters}
            className="btn-secondary h-10"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3">Carregando entradas...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            <p>Erro ao carregar dados.</p>
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-primary-300" />
            </div>
            <p className="text-gray-500 font-medium">Nenhuma entrada registrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-primary-50/50">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                    <th className="px-6 py-3">Data</th>
                    <th className="px-4 py-3">Descrição</th>
                    <th className="px-4 py-3">Método</th>
                    <th className="px-4 py-3">Conta</th>
                    <th className="px-4 py-3 text-right">Bruto</th>
                    <th className="px-4 py-3 text-right">Imposto</th>
                    <th className="px-4 py-3 text-right">Líquido</th>
                    {canManage && <th className="px-4 py-3 w-12" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {items.map(item => {
                    const isBalanced = item.linked_transaction_id != null;
                    const isDirectPayment = item.balance_type && (item.balance_type === 'PIX_SUPPLIER' || item.balance_type === 'CHEQUE_SUPPLIER');
                    const isManual = item.origin_source_type === 'MANUAL';
                    return (
                    <tr key={item.id} className={`hover:bg-primary-50/30 transition-colors ${isBalanced ? 'bg-orange-50' : ''}`}>
                      <td className="px-6 py-4 text-sm text-gray-600">{formatLocalDate(item.date)}</td>
                      <td className="px-4 py-4 text-sm">
                        {item.project_id && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                            Projeto #{item.project_id}
                          </span>
                        )}
                        {isManual && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 mr-2">
                            Entrada Manual
                          </span>
                        )}
                        {isDirectPayment && !isBalanced && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            PIX/Cheque direto
                          </span>
                        )}
                        <div className="font-medium text-gray-900">{item.description}</div>
                        {item.party_name && (
                          <div className="text-xs text-gray-500 mt-0.5">Cliente: {item.party_name}</div>
                        )}
                        {item.project_description && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{item.project_description}</div>
                        )}
                        {isBalanced && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            🔗 Balanceado
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isBalanced ? 'bg-orange-100 text-orange-700' : 'bg-primary-100 text-primary-700'}`}>
                          {item.payment_method_code || item.payment_method || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">{item.account_name} → {item.receiver_account_name}</td>
                      <td className="px-4 py-4 text-sm text-right">R$ {parseFloat(item.gross_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-4 text-sm text-right text-gray-500">{parseFloat(item.tax_percent || 0).toFixed(2)}%</td>
                      <td className="px-4 py-4 text-sm font-semibold text-right text-primary-600">
                        R$ {parseFloat(item.net_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      {canManage && (
                        <td className="px-4 py-4">
                          {item.source_type === 'income' && (
                            <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" onClick={()=> deleteMutation.mutate(item.id)}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          {item.source_type === 'cashbox_transaction' && (
                            <span className="text-xs text-orange-600">Caixa</span>
                          )}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Paginação */}
            {pagination.pages > 1 && (
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Mostrando <span className="font-semibold">{(pagination.page - 1) * pagination.limit + 1}</span> a{' '}
                  <span className="font-semibold">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> de{' '}
                  <span className="font-semibold">{pagination.total}</span> registros
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {pagination.page} de {pagination.pages}
                  </span>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.pages}
                    className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FinanceEntries;